import React, { useEffect, useMemo, useState } from 'react'
import chroma from 'chroma-js'
import { mapStateManager } from '../../MapStateManager'

/** Match frontend SvgSeatmap `Seats/Seat/Seat.js` for NMSU-style venues. */
const LEGACY_SEAT_SCALE = 1.2
const FRONTEND_SEAT_SCALE = 0.95
const FRONTEND_DEFAULT_BORDER_RADIUS = 33
const PURCHASER_GREEN = '#45B26B'
const PURCHASER_HALO_FILL = 'rgba(69, 178, 107, 0.22)'
const CLUSTER_HALO_SCALE_SELECTED = 1.75
const CLUSTER_HALO_SCALE_HOVER = 1.55

const darkenColor = (color, amount) => {
  return chroma(color).darken(amount).hex()
}

function Seat({
  seat,
  getSeatState,
  useFrontendSeatSizing = false,
  seatScale,
  seatBorderRadius,
  onClick,
  onMouseEnter,
  onMouseLeave,
}) {
  const hasVenueSeatScale = typeof seatScale === 'number'
  const SEAT_SCALE = hasVenueSeatScale
    ? seatScale
    : useFrontendSeatSizing
      ? FRONTEND_SEAT_SCALE
      : LEGACY_SEAT_SCALE
  const borderRadiusBase = useFrontendSeatSizing
    ? typeof seatBorderRadius === 'number'
      ? seatBorderRadius
      : FRONTEND_DEFAULT_BORDER_RADIUS
    : 20
  const scaledCornerRadius =
    useFrontendSeatSizing || hasVenueSeatScale
      ? borderRadiusBase * SEAT_SCALE
      : 20 * SEAT_SCALE
  const seatStatus = getSeatState(seat.seatId) || {
    disabled: false,
    color: '#c6c8cc',
  }
  const [seatState, setSeatState] = useState(
    mapStateManager.getState(seat.seatId)
  )

  useEffect(() => {
    return mapStateManager.subscribe((updatedSeatId) => {
      if (updatedSeatId === seat.seatId) {
        setSeatState(mapStateManager.getState(seat.seatId))
      }
    })
  }, [seat.seatId])

  const isAccessible =
    Boolean(
      seat?.accessibleType || seat?.accessiblityType || seat?.accessibilityType
    ) || Boolean(seat?.accessible)

  const accessibleIconNode = useMemo(() => {
    if (!isAccessible) return null

    const scaledW = seat.w * SEAT_SCALE
    const scaledH = seat.h * SEAT_SCALE
    const scaledX = seat.cx - (scaledW - seat.w) / 2
    const scaledY = seat.cy - (scaledH - seat.h) / 2

    const iconSize = Math.min(scaledW, scaledH) * 0.6
    const iconOffsetX = scaledX + (scaledW - iconSize) / 2
    const iconOffsetY = scaledY + (scaledH - iconSize) / 2

    return (
      <use
        key="accessible-icon"
        href="#admin-seatmap-icon-accessible"
        x={iconOffsetX}
        y={iconOffsetY}
        width={iconSize}
        height={iconSize}
        pointerEvents="none"
      />
    )
  }, [isAccessible, seat, SEAT_SCALE])

  const scaledW = seat.w * SEAT_SCALE
  const scaledH = seat.h * SEAT_SCALE
  const scaledX = seat.cx - (scaledW - seat.w) / 2
  const scaledY = seat.cy - (scaledH - seat.h) / 2
  // cx/cy are the unscaled seat origin; the drawn rect is centered on scaled bounds.
  const centerX = scaledX + scaledW / 2
  const centerY = scaledY + scaledH / 2

  const clusterHighlight = seatStatus?.clusterHighlight
  const isClusterAnchor = Boolean(seatStatus?.isClusterAnchor)
  const isClusterSelected = clusterHighlight === 'selected'
  const isClusterHover = clusterHighlight === 'hover'
  const isDragSelected =
    seatState?.selected && !isClusterSelected && !seatStatus?.disabled

  const fill = isClusterSelected
    ? PURCHASER_GREEN
    : isDragSelected
      ? darkenColor(seatStatus.color, 1)
      : seatStatus.color

  const coreRadius = Math.min(scaledW, scaledH) / 2
  const showClusterHalo = isClusterSelected || isClusterHover
  const haloRadius =
    coreRadius *
    (isClusterSelected
      ? isClusterAnchor
        ? CLUSTER_HALO_SCALE_SELECTED
        : CLUSTER_HALO_SCALE_HOVER
      : CLUSTER_HALO_SCALE_HOVER)

  return (
    <g data-interactive-seat="true">
      {showClusterHalo && (
        <circle
          cx={centerX}
          cy={centerY}
          r={haloRadius}
          fill={PURCHASER_HALO_FILL}
          stroke={PURCHASER_GREEN}
          strokeWidth={1}
          pointerEvents="none"
        />
      )}
      <rect
        x={scaledX}
        y={scaledY}
        id={seat.seatId}
        rx={scaledCornerRadius}
        ry={scaledCornerRadius}
        className="seat"
        width={scaledW}
        height={scaledH}
        pointerEvents="auto"
        style={{
          cursor: seatStatus?.disabled ? 'default' : 'pointer',
          fill,
          stroke: 'transparent',
          strokeWidth: 0,
        }}
        onClick={() => onClick?.(seat.seatId)}
        onMouseEnter={(event) => onMouseEnter?.(event, seat.seatId)}
        onMouseLeave={() => onMouseLeave?.(seat.seatId)}
      />
      {accessibleIconNode}
      {seatStatus?.additionalLabel && (
        <circle
          cx={scaledX + scaledW - Math.min(scaledW, scaledH) * 0.18}
          cy={scaledY + Math.min(scaledW, scaledH) * 0.18}
          r={Math.min(scaledW, scaledH) * 0.12}
          fill="#F4BC16"
          stroke="#141416"
          strokeWidth={0.6}
          pointerEvents="none"
        />
      )}
      {seatStatus.holdLetter && (
        <text
          x={centerX}
          y={centerY}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={Math.min(scaledW, scaledH) * 0.6}
          fontWeight="bold"
          fill="white"
          pointerEvents="none"
          style={{ userSelect: 'none' }}
        >
          {seatStatus.holdLetter}
        </text>
      )}
    </g>
  )
}

export default React.memo(Seat)
